import { useEffect, useState, useRef } from "react";
import { addMinutes, isAfter, parseISO } from "date-fns";

interface UseReservationCountdownProps {
  updatedAt: string | undefined;
  status: string | undefined;
  onExpired?: () => void;
}

interface UseReservationCountdownReturn {
  countdown: string | null;
  isExpired: boolean;
  minutesRemaining: number;
  secondsRemaining: number;
  isWarning: boolean;
  shouldShowWarning: boolean;
  expiryTime: Date | null;
}

const EXPIRY_MINUTES = 10;
const WARNING_THRESHOLD_MINUTES = 2;

/**
 * Hook for managing countdown on "initiated" reservations
 *
 * @param updatedAt - Timestamp when the reservation was last updated
 * @param status - Reservation status (only "initiated" counts down)
 * @param onExpired - Callback invoked when time expires (should be memoized with useCallback)
 *
 * @returns Countdown information including time remaining, warning status, and exact expiry time
 *
 * @example
 * ```tsx
 * const { countdown, isWarning, expiryTime } = useReservationCountdown({
 *   updatedAt: reservation.updatedAt,
 *   status: reservation.status,
 *   onExpired: useCallback(() => {
 *     navigate('/identifier');
 *   }, [navigate])
 * });
 * ```
 */
export const useReservationCountdown = ({
  updatedAt,
  status,
  onExpired,
}: UseReservationCountdownProps): UseReservationCountdownReturn => {
  const [countdown, setCountdown] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(EXPIRY_MINUTES);
  const [secondsRemaining, setSecondsRemaining] = useState(EXPIRY_MINUTES * 60);
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);

  const onExpiredRef = useRef(onExpired);
  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  useEffect(() => {
    // Only run countdown for "initiated" reservations
    if (status !== "initiated" || !updatedAt) {
      setCountdown(null);
      setIsExpired(false);
      setMinutesRemaining(EXPIRY_MINUTES);
      setSecondsRemaining(EXPIRY_MINUTES * 60);
      setExpiryTime(null);
      return;
    }

    const expiry = addMinutes(parseISO(updatedAt), EXPIRY_MINUTES);
    setExpiryTime(expiry);

    const updateCountdown = (): boolean => {
      const now = new Date();

      if (isAfter(now, expiry)) {
        // Expired!
        setIsExpired(true);
        setCountdown(null);
        setMinutesRemaining(0);
        setSecondsRemaining(0);
        onExpiredRef.current?.();
        return true;
      }

      const remainingMs = Math.max(expiry.getTime() - now.getTime(), 0);
      const remainingMinutes = Math.ceil(remainingMs / (1000 * 60));
      const remainingSeconds = Math.ceil(remainingMs / 1000);

      setIsExpired(false);
      setMinutesRemaining(remainingMinutes);
      setSecondsRemaining(remainingSeconds);

      const mins = Math.floor(remainingSeconds / 60);
      const secs = remainingSeconds % 60;
      const formattedCountdown = `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;

      setCountdown(formattedCountdown);

      return false;
    };

    if (updateCountdown()) return;

    const interval = setInterval(() => {
      const done = updateCountdown();
      if (done) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [updatedAt, status]);

  return {
    countdown,
    isExpired,
    minutesRemaining,
    secondsRemaining,
    isWarning:
      status === "initiated" &&
      secondsRemaining <= WARNING_THRESHOLD_MINUTES * 60 &&
      !isExpired,
    expiryTime,
    shouldShowWarning:
      status === "initiated" &&
      secondsRemaining === WARNING_THRESHOLD_MINUTES * 60 &&
      !isExpired,
  };
};
